import { chains } from '@emberswap/emberswap-sdk2/dist/constants'
import { Chain, Token } from '@emberswap/emberswap-sdk2/dist/entities'
import { Type } from '@emberswap/emberswap-sdk2/dist/types'
import { TokenList } from '@emberswap/emberswap-sdk2/dist/types/token-lists'
import Image from 'next/image'
import Link from 'next/link'
import React, { FC, ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import AutoSizer from 'react-virtualized-auto-sizer'

import { EMBERSWAP_LIST, UNSUPPORTED_LIST_URLS } from '../../constants/token-lists'
import { classNames } from '../../functions'
import { parseENSAddress } from '../../functions'
import { uriToHttp } from '../../functions/convert'
import { useIsMounted } from '../../hooks'
import { useFetchListCallback } from '../../hooks/useFetchListCallback'
import { disableList, enableList, removeList } from '../../state/lists/actions'
import { useActiveListUrls, useAllLists } from '../../state/lists/hooks'
import useActiveWeb3React from '../../wagmi/hooks/useActiveWeb3React'
import Button from '../Button'
import { Currency } from '../Currency'
import { Dialog } from '../Dialog'
import {
  ExternalLinkIcon,
  IconButton,
  NetworkIcon,
  SearchIcon,
  WarningIconRound,
  XCircleIcon,
} from '../Icons'
import { DEFAULT_INPUT_UNSTYLED, Input } from '../Input'
import { List } from '../List'
import Switch from '../Switch'
import { Tab } from '../Tabs'
import { TokenSelectorSettingsOverlayProps } from './TokenSelectorSettingsOverlay'

type TokenSelectorCustomTokensRowProps = {
  currency: Type
  onRemove(): void
}

export const TokenSelectorCustomTokensRow: FC<TokenSelectorCustomTokensRowProps> = ({
  currency,
  onRemove,
}) => {
  return (
    <div className="flex items-center w-full p-4">
      <div className="flex items-center justify-between flex-grow gap-2 rounded cursor-pointer">
        <div className="flex flex-row items-center flex-grow gap-2">
          <div className="w-7 h-7">
            <Currency.Icon disableLink currency={currency} width={28} height={28} />
          </div>
          <div className="flex flex-col items-start">
            <a className="text-xs font-medium text-slate-200">{currency.symbol}</a>
            <div className="flex gap-1">
              <NetworkIcon type="naked" chainId={currency.chainId} width={14} height={14} />
              <a className="text-xxs text-slate-500">{chains[currency.chainId].name}</a>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <IconButton onClick={onRemove}>
            <XCircleIcon width={20} height={20} className="text-slate-500" />
          </IconButton>
          <IconButton
            as="a"
            rel="noopener noreffer"
            target="_blank"
            href={chains[currency.chainId].getTokenUrl(currency.wrapped.address)}
          >
            <ExternalLinkIcon width={20} height={20} className=" fill-blue-500" />
          </IconButton>
        </div>
      </div>
    </div>
  )
}

type TokenSelectorCustomTokensOverlayProps = Pick<
  TokenSelectorSettingsOverlayProps,
  'customTokensMap' | 'onRemoveToken'
> 

export const TokenSelectorCustomTokensOverlay: FC<TokenSelectorCustomTokensOverlayProps> = ({
  customTokensMap,
  onRemoveToken,
}) => {
  // used for import list
  const [importList, setImportList] = useState<TokenList | undefined>()
  const [listURL, setListUrl] = useState<string | undefined>()
  const isMounted = useIsMounted()
  const { publicClient } = useActiveWeb3React()
  const [listUrlInput, setListUrlInput] = useState<string>('')
  const lists = useAllLists()
  // user must accept
  const [confirmed, setConfirmed] = useState(false)
  const dispatch = useDispatch()

  // monitor is list is loading
  const [_addError, _setAddError] = useState<string | null>(null)

  const [ids, tokens] = useMemo(() => {
    const ids: string[] = []
    const tokens: Token[] = []
    if (customTokensMap) {
      Object.entries(customTokensMap).forEach(([k, v]) => {
        ids.push(k)
        tokens.push(v)
      })
    }

    return [ids, tokens]
  }, [customTokensMap])
  const rowData = useMemo<TokenSelectorCustomTokensRowProps[]>(() => {
    if (!customTokensMap) return []

    return Object.values(customTokensMap).map((currency) => ({
      currency: currency,
      onRemove: () =>
        onRemoveToken({ chainId: currency.chainId, address: currency.wrapped.address }),
    }))
  }, [customTokensMap, onRemoveToken])

  // sort by active but only if not visible
  const activeListUrls = useActiveListUrls()
  const [activeCopy, setActiveCopy] = useState<string[] | undefined>()
  useEffect(() => {
    if (!activeCopy && activeListUrls) {
      setActiveCopy(activeListUrls)
    }
  }, [activeCopy, activeListUrls])

  const fetchList = useFetchListCallback(publicClient)

  const validUrl: boolean = useMemo(() => {
    return uriToHttp(listUrlInput).length > 0 || Boolean(parseENSAddress(listUrlInput))
  }, [listUrlInput])

  const sortedLists = useMemo(() => {
    const listUrls = Object.keys(lists || {}).filter((l) => l !== EMBERSWAP_LIST)
    return listUrls
      .filter((listUrl) => {
        // only show loaded lists, hide unsupported lists
        return Boolean(lists[listUrl]?.current) && !UNSUPPORTED_LIST_URLS.includes(listUrl)
      })
      .sort((u1, u2) => {
        const { current: l1 } = lists[u1]
        const { current: l2 } = lists[u2]

        // first filter on active lists
        if (activeCopy?.includes(u1) && !activeCopy?.includes(u2)) {
          return -1
        }
        if (!activeCopy?.includes(u1) && activeCopy?.includes(u2)) {
          return 1
        }

        if (l1 && l2) {
          return l1.name.toLowerCase() < l2.name.toLowerCase()
            ? -1
            : l1.name.toLowerCase() === l2.name.toLowerCase()
            ? 0
            : 1
        }
        if (l1) return -1
        if (l2) return 1
        return 0
      })
  }, [lists, activeCopy])
  // temporary fetched list for import flow
  const [tempList, setTempList] = useState<TokenList>()
  const [addError, setAddError] = useState<string | undefined>()

  useEffect(() => {
    /** */
    async function fetchTempList() {
      fetchList(listUrlInput, false)
        .then((list) => setTempList(list))
        .catch(() => setAddError('Error importing list'))
    }
    // if valid url, fetch details for card
    if (validUrl) {
      fetchTempList()
    } else {
      setTempList(undefined)
      listUrlInput !== '' && setAddError('Enter valid list location')
    }

    // reset error
    if (listUrlInput === '') {
      setAddError(undefined)
    }
  }, [fetchList, listUrlInput, validUrl])

  // check if list is already imported
  const isImported = Object.keys(lists).includes(listUrlInput)

  // set list values and have parent modal switch to import list view
  const handleImport = useCallback(() => {
    if (!tempList) return
    setImportList(tempList)
    // setModalView(CurrencyModalView.importList)
    setListUrl(listUrlInput)
  }, [listUrlInput, setImportList, setListUrl, tempList])
  
  
  const handleAddList = useCallback(() => {  
      if (listURL){
      const adding = Boolean(lists[listURL]?.loadingRequestId)

      if (adding){ return} else {
      _setAddError(null)
      fetchList(listURL)
        .then(() => {
          // turn list on
          dispatch(enableList(listURL))
          setListUrl('')
          setImportList(undefined)
          setListUrlInput('')
          setConfirmed(false)
          // go back to lists
          // setModalView(CurrencyModalView.manage)
        })
        .catch((error) => {
          _setAddError(error.message)
          dispatch(removeList(listURL))
        })
      }
      }
    }, [dispatch, fetchList, listURL, lists])
  const isImportList = useMemo(()=>(Boolean(listURL && importList)),[importList, listURL])

  const handleRemoveList = useCallback(
    (listUrl:string) => {
        dispatch(removeList(listUrl))
    },
    [dispatch]
  )
    const handleEnableList = useCallback((listUrl: string) => {

      dispatch(enableList(listUrl))
    }, [dispatch])

    const handleDisableList = useCallback(
      (listUrl: string) => {
        dispatch(disableList(listUrl))
      },
      [dispatch])
    
  const onClose = useCallback(() => {
    setListUrl('')
    setImportList(undefined)
    setConfirmed(false)
  },[])

  if (!isMounted) return <></>
  return (
    <div className="absolute top-0 left-0 w-full h-full p-6 mt-12 !bg-transparent">
      <Tab.Group>
        <Tab.List>
          <Tab>Tokens ({ids.length || '0'})</Tab>
          <Tab>Lists ({activeListUrls?.filter((l) => l !== EMBERSWAP_LIST).length || '0'})</Tab>
        </Tab.List>
        <Tab.Panels>
          <Tab.Panel>
            <div className="flex flex-col gap-2 px-3 py-2 mt-2 rounded-xl">
              <div className="relative min-h-[320px] rounded-t-none lg:max-h-[calc(100%-108px)] rounded-xl overflow-hidden h-full">
                {tokens.length === 0 && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="flex flex-col items-center justify-center gap-1">
                      <a className="flex text-xs italic text-primary">No custom tokens found</a>
                    </div>
                  </div>
                )}
                <Currency.List
                  className="scroll"
                  rowHeight={48}
                  rowRenderer={TokenSelectorCustomTokensRow}
                  rowData={rowData}
                />
              </div>
            </div>
          </Tab.Panel>
          <Tab.Panel>
            <div className="flex flex-col gap-2 px-3 py-2 mt-2 rounded-xl">
              <div
                className={classNames(
                  'relative flex items-center justify-between gap-2 px-3 mx-0  rounded-2xl py-1.5 text-primary bg-tokenselectorlist',
                  !importList && listUrlInput !== ''
                    ? '!ring-2 !ring-red-500 focus:!ring-transparent focus-within:!ring-secondarybg  selection:'
                    : importList
                    ? '!ring-2 !ring-green-500'
                    : 'focus-within:!ring-2 focus-within:!ring-secondarybg'
                )}
              >
                <Input.Address
                  type="text"
                  id="lists-importer"
                  placeholder="Add list by HTTPS, IPFS or ENS"
                  variant="unstyled"
                  value={listUrlInput ?? ''}
                  onChange={(val) => setListUrlInput(val)}
                  className={classNames(
                    DEFAULT_INPUT_UNSTYLED,
                    '!bg-transparent btn w-full flex group !text-primary  placeholder:text-slate-800 !font-medium  gap-2 cursor-pointer opacity-80 hover:opacity-100 hover:bg-opacity-70 px-4 h-[34px] rounded-xl text-base'
                  )}
                />
                {listUrlInput ? (
                  <XCircleIcon
                    width={20}
                    height={20}
                    className="cursor-pointer text-slate-500 hover:text-slate-300"
                    onClick={() => setListUrlInput('')}
                  />
                ) : (
                  <SearchIcon className="text-slate-500" strokeWidth={2} width={20} height={20} />
                )}
              </div>
              {addError && !tempList ? (
                <div title={addError} className="overflow-hidden text-red-500 text-ellipsis">
                  {addError}
                </div>
              ) : null}
              {tempList && !isImported ? (
                <TokenListLayout tokenList={tempList} listURL={listUrlInput}>
                  <></>
                  <div className="flex gap-1 px-2">
                    <Button
                      size="xs"
                      width="md"
                      variant="filled"
                      color="red"
                      onClick={handleImport}
                    >
                      Import
                    </Button>
                  </div>
                </TokenListLayout>
              ) : (
                <div className="h-full overflow-y-auto">
                  <div className="h-full min-h-[300px] pt-8">
                    <AutoSizer disableWidth>
                      {({ height }) => (
                        <div style={{ height }} className="space-y-2">
                          {sortedLists.map((listUrl) => {
                            const { current: list } = lists[listUrl]
                            return (
                              <>
                                {list && (
                                  <TokenListLayout key={listUrl} tokenList={list} listURL={listUrl}>
                                    <button
                                      onClick={() => handleRemoveList(listUrl)}
                                      className="right-0 pr-3 text-slate-700 hover:text-slate-600 justify-self-end text-end"
                                    >
                                      Remove
                                    </button>
                                    <div className="flex gap-1 pr-2">
                                      <Switch
                                        checked={Boolean(activeListUrls?.includes(listUrl))}
                                        onChange={() =>
                                          activeListUrls?.includes(listUrl)
                                            ? handleDisableList(listUrl)
                                            : handleEnableList(listUrl)
                                        }
                                        size="sm"
                                      />
                                    </div>
                                  </TokenListLayout>
                                )}
                              </>
                            )
                          })}
                        </div>
                      )}
                    </AutoSizer>
                  </div>
                </div>
              )}
            </div>
            <ImportDialog
              setConfirmed={setConfirmed}
              handleAddList={handleAddList}
              open={isImportList}
              onClose={onClose}
              importList={importList}
              listURL={listURL}
              confirmed={confirmed}
              _addError={_addError}
            />
          </Tab.Panel>
        </Tab.Panels>
      </Tab.Group>
    </div>
  )
}


const TokenListLayout: FC<{
  tokenList: TokenList
  listURL?: string
  children?: ReactNode[]
}> = ({ tokenList, listURL, children }) => {
  const listUrl = listURL ? `https://tokenlists.org/token-list?url=${listURL}` : ''
  return (
    <List className="transition rounded-xl items-center bg-tokenselectorlist  !flex-row !pt-0 px-2">
      {tokenList?.logoURI ? (
        <Image
          width={40}
          height={40}
          src={tokenList.logoURI}
          alt={`${tokenList?.name} list logo`}
        />
      ) : (
        <div style={{ width: '40px', height: '40px' }} />
      )}
      <List.Control className="relative flex flex-col flex-1 flex-grow !bg-transparent justify-center px-1 py-1">
        <Link
          rel="noreferrer"
          target="_blank"
          href={listUrl}
          className="overflow-hidden text-sm font-medium text-ellipsis"
        >
          {tokenList?.name}
        </Link>
        <div className="flex justify-between w-full text-xxs">
          {tokenList?.tokens.length} tokens
          {children?.[0]}
        </div>
        <p className="text-xxs text-primary">
          {tokenList?.tokens[0]?.chainId &&
            [...new Set(tokenList?.tokens.map((t) => t.chainId))].reduce(
              (accumulator, currentChainId, index) => {
                if (index === 0) {
                  return Chain.from(currentChainId).name
                } else {
                  return accumulator + ', ' + Chain.from(currentChainId).name
                }
              },
              ''
            )}
        </p>
      </List.Control>
      {children?.[1]}
    </List>
  )
}

const ImportDialog: FC<{
  handleAddList: () => void
  open: boolean
  onClose: () => void
  importList: TokenList | undefined
  listURL: string | undefined
  confirmed: boolean
  _addError: string | null
  setConfirmed: React.Dispatch<React.SetStateAction<boolean>>
}> = ({ handleAddList, onClose, open, importList, listURL, confirmed, _addError, setConfirmed }) => {
  return (
    <Dialog onClose={onClose} open={open}>
      <Dialog.Content className="!bg-tokenselector gradient-backdrop-blur !mb-0">
        <Dialog.Header onClose={onClose} title="Import List" />
        <List className="transition overflow-hidden relative lg:max-h-[calc(100%-108px)] border-t  -ml-3 -mr-3 p-4 rounded-t-none  border-slate-200/5 rounded-xl items-center !pt-0 px-2">
          <div className="flex flex-col w-auto h-auto gap-3 p-4">
            {importList && <TokenListLayout tokenList={importList} listURL={listURL} />}
            <div className="grid justify-center grid-flow-col gap-4 mb-3 text-center">
              <WarningIconRound className="text-red-500" height={32} width={32} />
              <div className="text-lg font-medium text-red-500">Import at your own risk </div>
            </div>
            <div className="grid gap-4 mb-3 text-xs text-center text-primary">
              <div className="font-medium ">
                By adding this list you are implicitly trusting that the data is correct. Anyone can
                create a list, including creating fake versions of existing lists and lists that
                claim to represent projects that do not have one.
              </div>
              <div className="font-bold text-red-500">
                If you purchase a token from this list, you may not be able to sell it back.
              </div>
            </div>
            <div
              className="flex justify-center w-full p-0 cursor-pointer select-none wrap"
              onClick={() => setConfirmed(!confirmed)}
            >
              <input
                className="h-5 m-0 bg-red-500"
                name="confirmed"
                type="checkbox"
                checked={confirmed}
                onChange={() => setConfirmed(!confirmed)}
              />
              <div className="text-primary ml-2.5 font-medium text-xxs">I understand</div>
            </div>
          </div>
          <Button
            fullWidth
            className="!rounded-xl !px-2"
            color="gradient"
            variant="outlined"
            size="xl"
            style={{
              borderRadius: '20px',
              padding: '10px 1rem',
            }}
            disabled={!confirmed}
            onClick={handleAddList}
          >
            Import
          </Button>
          {_addError && (
            <div title={_addError} className="overflow-hidden text-red-500 overflow-ellipsis">
              {_addError}
            </div>
          )}
        </List>
      </Dialog.Content>
    </Dialog>
  )
}